import { Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DeviceBrandChip from '@components/DeviceBrandChip';

import { useHealthChart } from '@hooks/useHealthChart';

const messages = defineMessages({
  sources: {
    id: 'healthTracker.detail.sources',
    defaultMessage: 'Sources',
  },
});

/**
 * The brands behind the readings currently in range.
 *
 * Names each brand on the view rather than leaving attribution to the Source
 * column, which a chart-view reader never sees. Built from the unfiltered set
 * so it does not change as the reader narrows the source filter.
 *
 * Not gated on category: a reading carries a source or it does not, and vital
 * signs hold device readings too (Garmin reports heart rate and SpO2). With
 * nothing to name, this renders nothing.
 */
export default function SourceBrands() {
  const { formatMessage } = useIntl();
  const { completeData } = useHealthChart();

  const brands = new Map<string, string | undefined>();
  completeData.forEach(({ record, source }) => {
    if (source && source !== '-' && !brands.has(source)) {
      brands.set(source, record.source ?? undefined);
    }
  });

  if (!brands.size) {
    return null;
  }

  return (
    <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
      <Typography color="text.primary" variant="overline">
        {formatMessage(messages.sources)}
      </Typography>
      {[...brands].map(([name, slug]) => (
        <DeviceBrandChip key={`source-brand-${name}`} name={name} slug={slug} />
      ))}
    </Stack>
  );
}
